import React, { useState } from 'react';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { Building2, Users, Truck, Percent, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Wholesale = () => {
  const { getCartItemCount } = useCart();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    businessType: '',
    estimatedVolume: '',
    message: ''
  });

  const benefits = [
    {
      icon: Percent,
      title: 'Trade Pricing',
      description: 'Competitive wholesale rates for qualified businesses'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Personal account manager for your business'
    },
    {
      icon: Truck,
      title: 'Priority Fulfillment',
      description: 'Fast turnaround on bulk orders'
    },
    {
      icon: Building2,
      title: 'Co-Branding',
      description: 'Contact us to discuss partnership opportunities'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact/wholesale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: "Application Received!",
          description: "We'll review your details and get back to you within 2 business days.",
        });
        setFormData({
          businessName: '',
          contactName: '',
          email: '',
          phone: '',
          businessType: '',
          estimatedVolume: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Failed to submit application');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-24 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="px-6 py-2 bg-[#DB5A14] text-white font-black text-sm tracking-widest">
                FOR BUSINESSES
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Wholesale & <span className="text-[#F39C49]">Trade Accounts</span>
            </h1>
            <p className="text-xl text-[#CFD2D4] max-w-2xl mx-auto">
              Outfit your entire team with professional women's workwear. 
              Special pricing for collision repair shops, training centers, and stockists.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-[#004D51] mb-12 text-center">
            Trade Account Benefits
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white border-4 border-[#004D51] rounded-xl p-6 text-center hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 bg-[#DB5A14] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-black text-[#004D51] mb-3">{benefit.title}</h3>
                <p className="text-[#545759]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Qualifies */}
      <section className="py-24 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">
              Who Qualifies?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Collision repair & body shops',
                'Collision industry suppliers',
                'TAFE & trade training centers',
                'Workwear distributors',
                'Fleet & uniform buyers',
                'Industry associations'
              ].map((item, index) => (
                <div key={index} className="flex items-center bg-white rounded-lg p-4 border-2 border-[#004D51]">
                  <CheckCircle className="w-6 h-6 text-[#DB5A14] mr-3 flex-shrink-0" />
                  <span className="text-[#004D51] font-bold">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-black text-[#004D51] mb-4 text-center">
              Apply for Trade Account
            </h2>
            <p className="text-[#545759] text-center mb-12">
              Fill out the form below and we'll get back to you within 2 business days.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Business Name *
                  </label>
                  <Input
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="Your Business Name"
                    data-testid="wholesale-business-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Contact Name *
                  </label>
                  <Input
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="Your Name"
                    data-testid="wholesale-contact-name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="email@business.com"
                    data-testid="wholesale-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="(04) XXXX XXXX"
                    data-testid="wholesale-phone"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Business Type *
                  </label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border-2 border-[#004D51] rounded-md focus:border-[#DB5A14] focus:outline-none bg-white"
                    data-testid="wholesale-business-type"
                  >
                    <option value="">Select type...</option>
                    <option value="collision-repair">Collision Repair Shop</option>
                    <option value="supplier">Paint/Equipment Supplier</option>
                    <option value="training">TAFE/Training Center</option>
                    <option value="distributor">Workwear Distributor</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Estimated Annual Volume
                  </label>
                  <select
                    name="estimatedVolume"
                    value={formData.estimatedVolume}
                    onChange={handleChange}
                    className="w-full h-12 px-4 border-2 border-[#004D51] rounded-md focus:border-[#DB5A14] focus:outline-none bg-white"
                    data-testid="wholesale-volume"
                  >
                    <option value="">Select volume...</option>
                    <option value="5-10">5-10 units</option>
                    <option value="10-25">10-25 units</option>
                    <option value="25-50">25-50 units</option>
                    <option value="50+">50+ units</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Tell Us About Your Business
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="min-h-32 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="How many female technicians do you have? Any specific requirements?"
                  data-testid="wholesale-message"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg disabled:opacity-70"
                data-testid="wholesale-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-[#004D51] text-white">
        <div className="container mx-auto px-6 text-center">
          <p className="text-xl">
            Prefer to chat? Email us at{' '}
            <a href="mailto:hello@vestaworkwear.com.au" className="text-[#F39C49] font-bold hover:text-white">
              hello@vestaworkwear.com.au
            </a>
          </p>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default Wholesale;
